/**
 * Entity Detail Panel — Types
 *
 * Type definitions for the enhanced entity detail panel system.
 * Used by EntityDetailPanel, useEntityDetailPanel, and all tab components.
 *
 * Tab metadata (labels, icons, order, contextual visibility) lives in
 * tabRegistry.ts — the single source of truth for tab definitions.
 */

import type { PrimitiveType } from '@/config/primitive-metadata';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// Re-export tab system from registry (single source of truth)
export type { DetailTab } from './tabRegistry';
export {
  TAB_REGISTRY,
  TAB_LABELS,
  TAB_ICONS,
  DEFAULT_TAB_ORDER,
  CONTEXTUAL_TABS,
  getVisibleTabs,
} from './tabRegistry';

// ============================================================================
// PANEL LAYOUT
// ============================================================================

/** Panel dock position */
export type DockPosition = 'right' | 'bottom';

/** Tab orientation — horizontal (top) or vertical (side menu) */
export type TabOrientation = 'horizontal' | 'vertical';

/** Panel size preset */
export type PanelSize = 'compact' | 'standard' | 'wide' | 'fullscreen';

/** Size values for right-docked drawer */
export const PANEL_SIZE_MAP: Record<Exclude<PanelSize, 'fullscreen'>, string> = {
  compact: 'md',
  standard: 'lg',
  wide: 'xl',
};

/** Bottom dock height presets */
export const BOTTOM_DOCK_HEIGHT: Record<Exclude<PanelSize, 'fullscreen'>, string> = {
  compact: '30vh',
  standard: '45vh',
  wide: '60vh',
};

// ============================================================================
// TAB SYSTEM (supplementary types — definitions in tabRegistry.ts)
// ============================================================================

/** Tab visibility configuration per primitive type */
export interface TabVisibility {
  /** Tab ID */
  tab: import('./tabRegistry').DetailTab;
  /** Human-readable label */
  label: string;
  /** When true, tab is shown for this primitive */
  visible: boolean;
  /** When true, tab requires data loading */
  async?: boolean;
}

// ============================================================================
// QUICK ACTIONS
// ============================================================================

/** Quick action definition */
export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  /** Action handler */
  onClick: () => void;
  /** Color theme for the button */
  color?: string;
  /** When true, shows confirmation dialog before executing */
  requiresConfirmation?: boolean;
  /** Confirmation message */
  confirmMessage?: string;
  /** Keyboard shortcut */
  shortcut?: string;
  /** When true, action is disabled */
  disabled?: boolean;
}

// ============================================================================
// BREADCRUMB
// ============================================================================

/** Breadcrumb segment */
export interface BreadcrumbSegment {
  label: string;
  primitiveType?: PrimitiveType;
  entityId?: string;
  /** When provided, breadcrumb is clickable */
  onClick?: () => void;
}

// ============================================================================
// NAVIGATION
// ============================================================================

/** Row navigation state for arrow-key support */
export interface RowNavigationState {
  /** Current row index in the filtered dataset */
  currentIndex: number;
  /** Total rows in the filtered dataset */
  totalRows: number;
  /** Navigate to previous row */
  goToPrevious: () => void;
  /** Navigate to next row */
  goToNext: () => void;
  /** Navigate to first row */
  goToFirst: () => void;
  /** Navigate to last row */
  goToLast: () => void;
}

// ============================================================================
// PANEL PROPS
// ============================================================================

/** Custom tab content overrides — keyed by tab ID */
export type TabContentOverrides = Partial<Record<import('./tabRegistry').DetailTab, React.ReactNode>>;

/** Props for the EntityDetailPanel component */
export interface EntityDetailPanelProps {
  /** The selected entity to display */
  entity: PrimitiveItem | null;
  /** Primitive type of the selected entity */
  primitiveType: PrimitiveType;
  /** Whether the panel is open */
  opened: boolean;
  /** Close the panel */
  onClose: () => void;
  /** Whether the primitive is read-only (no edit tab) */
  isReadOnly?: boolean;
  /** Callback when entity is updated via edit tab */
  onEntityUpdated?: () => void;
  /** Callback when entity is archived via quick actions */
  onEntityArchived?: (entity: PrimitiveItem) => void;
  /** Row navigation support */
  navigation?: RowNavigationState;
  /** Breadcrumb trail */
  breadcrumbs?: BreadcrumbSegment[];
  /** Additional content for the edit tab */
  editContent?: React.ReactNode;
  /** Custom content for the details tab (replaces default flat key-value table) */
  detailsContent?: React.ReactNode;
  /** Status color mapping override */
  statusColorMap?: Record<string, string>;
  /** All entities of the same type for compare tab selector */
  allEntities?: PrimitiveItem[];
  /**
   * Custom content overrides for any tab. Keyed by tab ID.
   * Use this instead of adding individual *Content props.
   *
   * @example
   * tabContentOverrides={{
   *   dependencies: <TaskDependencyTab ... />,
   *   'workflow-map': <TaskWorkflowMapTab ... />,
   * }}
   */
  tabContentOverrides?: TabContentOverrides;
  /** @deprecated Use tabContentOverrides.dependencies instead */
  dependenciesContent?: React.ReactNode;
  /** @deprecated Use tabContentOverrides['workflow-map'] instead */
  workflowMapContent?: React.ReactNode;
  /** Additional action buttons rendered in the QuickActionsBar at the bottom of the panel */
  extraQuickActions?: React.ReactNode;
}

/** State returned by useEntityDetailPanel hook */
export interface EntityDetailPanelState {
  /** Current active tab */
  activeTab: import('./tabRegistry').DetailTab;
  /** Set active tab */
  setActiveTab: (tab: import('./tabRegistry').DetailTab) => void;
  /** Current dock position */
  dockPosition: DockPosition;
  /** Set dock position */
  setDockPosition: (pos: DockPosition) => void;
  /** Current panel size */
  panelSize: PanelSize;
  /** Set panel size */
  setPanelSize: (size: PanelSize) => void;
  /** Whether panel is in fullscreen mode */
  isFullscreen: boolean;
  /** Toggle fullscreen */
  toggleFullscreen: () => void;
  /** Tab orientation (horizontal top bar or vertical side menu) */
  tabOrientation: TabOrientation;
  /** Toggle tab orientation */
  toggleTabOrientation: () => void;
}

// ============================================================================
// STORAGE KEYS
// ============================================================================

/** localStorage key for panel preferences */
export const STORAGE_KEY_PREFIX = 'ferp:entity-detail';

export function getStorageKey(key: string): string {
  return `${STORAGE_KEY_PREFIX}:${key}`;
}
